<template>
  <div
    v-if="blok.Image && blok.Image[0] && blok.CtaButton"
    v-editable
    class="relative flex h-[500px] flex-col items-center justify-center bg-cover bg-no-repeat md:h-[600px]"
    :class="[marginClasses, { 'mx-break-out': blok.wholeWidth }]"
  >
    <NuxtLazyImage :source="imageSrc" :alt="imageAlt" cover />
    <div
      class="absolute inset-0 bg-gradient-to-t from-black-40 md:hidden"
    ></div>
    <div
      class="absolute inset-0 flex flex-col justify-center text-center text-white"
    >
      <Responsive :base-width="410" :initial-height="410" align="center">
        <h2 class="text-[34px] font-bold md:-mb-5 md:text-[60px]">
          {{ blok.preline }}
        </h2>
        <h3 class="font-handwritten text-[58px] md:text-[80px]">
          {{ blok.headline }}
        </h3>
      </Responsive>

      <Intersect
        v-for="button in blok.CtaButton"
        :key="button._uid"
        :threshold="0.5"
        @enter.once="handleVisibility(button)"
      >
        <component
          :is="button.component"
          class="text-white"
          :blok="button"
          @click="handleClick(button)"
        />
      </Intersect>
    </div>
  </div>
</template>

<script lang="ts">
import useStoryblokMargins from '~/cms/storyblok/composables/useStoryblokMargins'
import NuxtLazyImage from '~/components/images/NuxtLazyImage.vue'
import useTrackingEvents from '~/composables/tracking/useTrackingEvents'
import { useDeviceType } from '~/composables/useDeviceType'
import Responsive from '#layers/base/components/utils/Responsive.vue'
import { CTA_PROMOTION_CONTEXT_KEY } from '#layers/base/constants/injectionKeys'
import type {
  SbCampaignTeaser,
  SbCtaButton,
} from '~/cms/storyblok/types/storyblok.d.ts'

export default defineComponent({
  name: 'CampaignTeaser',
  components: { NuxtLazyImage, Responsive },
  props: {
    blok: {
      type: Object as PropType<SbCampaignTeaser>,
      default: () => {},
    },
  },
  setup(props) {
    const { trackPromotionSelect, trackPromotionView } = useTrackingEvents()
    const { isMobile } = useDeviceType()

    const { marginClasses } = useStoryblokMargins(props.blok)

    // This block emits the promotion events for the CTA it nests, using its own
    // parameters, so the CTA must not emit competing ones (ECOM-2766).
    provide(CTA_PROMOTION_CONTEXT_KEY, { claimedBy: 'campaignteaser' })

    return {
      imageSrc: computed(() => {
        const image = props.blok.Image?.[0]
        if (!image) {
          return ''
        }
        return isMobile.value && image.imageMobile?.filename
          ? image.imageMobile.filename
          : image.image?.filename || ''
      }),
      imageAlt: computed(() => props.blok.Image?.[0]?.altDescription),
      handleClick: (_link: SbCtaButton) => {
        trackPromotionSelect({
          id: props.blok.promotion_id || 'campaign_teaser_promotion_id',
          name: props.blok.promotion_name || 'campaign_teaser_promotion_name',
          promoType: 'campaignteaser',
          position: 1,
          creativeNameSb: props.blok.creative_name || '',
        })
      },
      handleVisibility: (_link: SbCtaButton) => {
        trackPromotionView({
          id: props.blok.promotion_id || 'campaign_teaser_promotion_id',
          name: props.blok.promotion_name || 'campaign_teaser_promotion_name',
          promoType: 'campaignteaser',
          position: 1,
          creativeNameSb: props.blok.creative_name || '',
        })
      },
      marginClasses,
    }
  },
})
</script>
